import { formatDateTime } from "../../utils/DateTime";
import { useEffect, useState } from "react";
import { InventoryWareHouseService } from "../../services/InventoryWareHouseService";
import { RoleService } from "../../services/RoleService";
import { loadAllLookupPages } from "../../hooks/useInventoryLookups";
import type { InventoryWareHouse } from "../../models/InventoryWareHouse";
import type { KeyValue } from "../../models/KeyValue";
import { useWarehouse } from "../../warehouse/WarehouseContext";
import ManagementPageHTML, {
  IdentityCell,
  StatusBadge,
} from "../../components/ManagementPage/ManagementPageHTML";
import type {
  ManagementColumn,
  ManagementDialogField,
} from "../../components/ManagementPage/ManagementPageTypes";
import type { SystemUser } from "../../models/SystemUser";
import { UserService } from "../../services/UserService";
import "./UserCss.css";
const columns: ManagementColumn<SystemUser>[] = [
  {
    Key: "User",
    Header: "User",
    Render: (row) => (
      <IdentityCell
        Icon="fa-user"
        Title={`${row.FirstName} ${row.LastName}`}
        Subtitle={row.Code}
      />
    ),
  },
  { Key: "Email", Header: "Email", Render: (row) => row.Email },
  { Key: "Phone", Header: "Phone", Render: (row) => row.PhoneNumber || "—" },
  {
    Key: "Roles",
    Header: "Roles",
    Render: (row) => row.RoleCodes?.join(", ") || "None",
  },
  {
    Key: "Login",
    Header: "Last login",
    Render: (row) =>
      row.LastLoginAt
        ? formatDateTime(row.LastLoginAt)
        : "Never",
  },
  {
    Key: "Status",
    Header: "Status",
    Render: (row) => (
      <StatusBadge Status={row.IsActive ? "Active" : "InActive"} />
    ),
  },
];
const fields: ManagementDialogField<SystemUser>[] = [
  { Key: "Code", Label: "User code", DisableAfterNew: true },
  { Key: "FirstName", Label: "First name", Required: true },
  { Key: "LastName", Label: "Last name", Required: true },
  { Key: "Email", Label: "Email", Type: "email", Required: true },
  { Key: "PhoneNumber", Label: "Phone number", Type: "tel" },
  {
    Key: "Password",
    Label: "Temporary password",
    Required: true,
    RemoveAfterNew: true,
  },
  {
    Key: "RoleCodes",
    Label: "Assigned roles",
    Type: "multiselect",
    Options: [],
    FullWidth: true,
  },
  { Key: "IsActive", Label: "Active", Type: "switch" },
  { Key: "IsCustomer", Label: "Customer user", Type: "switch" },
];
export default function UserHTML() {
  const [warehouses, setWarehouses] = useState<InventoryWareHouse[]>([]);
  const [roles, setRoles] = useState<KeyValue[]>([]);
  const [error, setError] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(true);
  const { Reload } = useWarehouse();
  useEffect(() => {
    let active = true;
    void Promise.all([
      loadAllLookupPages(InventoryWareHouseService().GetAll),
      loadAllLookupPages(RoleService().GetAll),
    ])
      .then(([w, r]) => {
        if (active) {
          setWarehouses(w);
          setRoles(r.filter(role=>role.IsActive && !/tenant[\s_-]*admin/i.test(`${role.Code} ${role.Name}`)).map(role=>({Key:role.Code,Value:role.Name})));
          setOptionsLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError(
            "Could not load warehouse or role options. Reload before changing assignments.",
          );
          setOptionsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);
  const dialogFields: ManagementDialogField<SystemUser>[] = [
    ...fields.map((x) =>
      x.Key === "RoleCodes"
        ? {
            ...x,
            Options: roles.map((r) => ({
              display: String(r.Value),
              value: r.Key,
              other: null,
            })),
          }
        : x,
    ),
    {
      Key: "WareHouses",
      Label: "Assigned warehouses",
      Type: "multiselect",
      FullWidth: true,
      Options: warehouses.map((x) => ({
        display: `${x.Name}${x.Status !== "Active" ? " (inactive)" : ""}`,
        value: x.Code,
        other: null,
      })),
    },
  ];
  return (
    <ManagementPageHTML
      Service={UserService()}
      Title="Users"
      Eyebrow="Access management"
      Description="Manage user accounts, role assignments and warehouse access."
      AddLabel="Add user"
      SearchPlaceholder="Search name, email or phone"
      Rows={[]}
      Columns={[
        ...columns.filter((c) => c.Key !== "Roles"),
        {
          Key: "Roles",
          Header: "Roles",
          Render: (x) => (
            <span className="warehouse-chips">
              {(x.RoleCodes ?? []).map((code) => (
                <span className="management-status" key={code}>
                  {String(
                    roles.find((role) => role.Key === code)?.Value ?? code,
                  )}
                </span>
              ))}
            </span>
          ),
        },
        {
          Key: "WareHouses",
          Header: "Warehouses",
          Render: (x) => (
            <span className="warehouse-chips">
              {(x.WareHouses ?? []).map((c) => (
                <span key={c} className="management-status">
                  {warehouses.find((w) => w.Code === c)?.Name ?? c}
                </span>
              ))}
            </span>
          ),
        },
      ]}
      DialogFields={dialogFields}
      OnSaved={() => Reload()}
      DefaultValues={{ WareHouses: [], RoleCodes: [], IsActive: true }}
      CanCreate={!error && !optionsLoading}
      CanEdit={!error && !optionsLoading}
      ValidateForm={(): Record<string, string> =>
        error ? { WareHouses: error } : {}
      }
      Statuses={["Active", "InActive"]}
      GetStatus={(row) => (row.IsActive ? "Active" : "InActive")}
      GetRowKey={(row) => row.Code}
      GetSearchText={(row) =>
        `${row.Code} ${row.FirstName} ${row.LastName} ${row.Email} ${row.RoleCodes?.join(" ")}`
      }
      Summaries={[]}
      GetSummaries={(rows, total) => [
        { Label: "Users", Value: total, Icon: "fa-users", Tone: "Blue" },
        {
          Label: "Active",
          Value: rows.filter((x) => x.IsActive).length,
          Icon: "fa-user-check",
          Tone: "Green",
        },
        {
          Label: "Tenant admins",
          Value: rows.filter((x) => x.IsTenantAdmin).length,
          Icon: "fa-user-shield",
          Tone: "Purple",
        },
        {
          Label: "Never logged in",
          Value: rows.filter((x) => !x.LastLoginAt).length,
          Icon: "fa-clock",
          Tone: "Orange",
        },
      ]}
      Filters={[
        {
          Key: "Admin",
          Label: "Access level",
          Options: ["Tenant admin", "Standard"],
          GetValue: (row) => (row.IsTenantAdmin ? "Tenant admin" : "Standard"),
        },
      ]}
    />
  );
}
